// inbox-89 / roadmap E3 — клиент для /api/v1/bio-pages.
// Backend ships в app/apps/api/bio.py (inbox-88).

import { ApiError } from './links';

// Re-export для view-уровневых импортов (BioListView и т.п.
// импортируют ApiError из @/api/bio один-неймспейсом).
export { ApiError } from './links';

// inbox-497/498/499 — расширенные типы под Bio Linkme-style.
// Backend контракт: см. docs/superpowers/specs/2026-05-22-bio-linkme-style-design.md
// Backend pending — frontend пишет на mock-данных с graceful-fallback.

export type DisplayNameOption = 'display_name' | 'username' | 'first_last';

export interface BioPagePixels {
  ymetrika_id?: string | null;
  vk_id?: string | null;
  ga_id?: string | null;
  fb_id?: string | null;
}

export interface BioAgeGate {
  enabled: boolean;
  min_age?: number;
}

export interface BioSettings {
  displayNameOption?: DisplayNameOption;
  bio_text?: string;
  show_visit_count?: boolean;
  hide_branding?: boolean;
  pixels?: BioPagePixels;
  age_gate?: BioAgeGate;
  social_in_header?: boolean;
}

export interface BioWidget {
  id: number;
  type: string;
  position: number;
  config_json: Record<string, unknown>;
}

export interface BioPage {
  id: number;
  organization_id: number;
  slug: string;
  theme_json: Record<string, unknown>;
  status: 'draft' | 'published' | 'archived';
  widgets: BioWidget[];
  // inbox-497 — расширенные поля профиля. Все optional для backward
  // compatibility — backend заполнит когда миграция применится.
  display_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  avatar_url?: string | null;
  cover_url?: string | null;
  accent_color?: string | null;
  bg_color?: string | null;
  verified?: boolean;
  visit_count?: number;
  access_token?: string | null;
  default_lang?: string;
  settings_json?: BioSettings;
  // Доступные языки (вернёт public-endpoint после translations CRUD).
  available_langs?: string[];
}

export interface BioPageCreate {
  slug: string;
  theme_json?: Record<string, unknown>;
  status?: BioPage['status'];
}

export interface BioPagePatch {
  slug?: string;
  theme_json?: Record<string, unknown>;
  status?: BioPage['status'];
  // inbox-499 — все новые поля доступны через PATCH /api/v1/bio/{id}.
  display_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  avatar_url?: string | null;
  cover_url?: string | null;
  accent_color?: string | null;
  bg_color?: string | null;
  default_lang?: string;
  settings_json?: BioSettings;
}

export interface WidgetCreate {
  type: string;
  config_json?: Record<string, unknown>;
}

export interface WidgetPatch {
  type?: string;
  config_json?: Record<string, unknown>;
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch('/api/v1/bio-pages' + path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    ...init,
  });
  if (!res.ok) {
    let detail = '';
    const cloned = res.clone();
    try {
      const j = await res.json();
      detail = j.detail || j.error || JSON.stringify(j);
    } catch {
      detail = await cloned.text();
    }
    throw new ApiError(res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const BioApi = {
  list(): Promise<BioPage[]> {
    return req<BioPage[]>('');
  },
  create(body: BioPageCreate): Promise<BioPage> {
    return req<BioPage>('', { method: 'POST', body: JSON.stringify(body) });
  },
  get(id: number): Promise<BioPage> {
    return req<BioPage>(`/${id}`);
  },
  patch(id: number, body: BioPagePatch): Promise<BioPage> {
    return req<BioPage>(`/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
  },
  delete(id: number): Promise<void> {
    return req<void>(`/${id}`, { method: 'DELETE' });
  },
  // Aliases для view-уровневого ergonomics (BioListView и т.п.).
  update(id: number, body: BioPagePatch): Promise<BioPage> {
    return req<BioPage>(`/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
  },
  remove(id: number): Promise<void> {
    return req<void>(`/${id}`, { method: 'DELETE' });
  },
  // Widgets
  addWidget(pageId: number, body: WidgetCreate): Promise<BioWidget> {
    return req<BioWidget>(`/${pageId}/widgets`, { method: 'POST', body: JSON.stringify(body) });
  },
  patchWidget(widgetId: number, body: WidgetPatch): Promise<BioWidget> {
    return req<BioWidget>(`/widgets/${widgetId}`, { method: 'PATCH', body: JSON.stringify(body) });
  },
  deleteWidget(widgetId: number): Promise<void> {
    return req<void>(`/widgets/${widgetId}`, { method: 'DELETE' });
  },
  reorderWidgets(pageId: number, items: { id: number; position: number }[]): Promise<void> {
    return req<void>(`/${pageId}/widgets/reorder`, {
      method: 'PATCH',
      body: JSON.stringify({ items }),
    });
  },
  // inbox-499 — privacy toggle + регенерация access_token (backend pending).
  regenerateToken(pageId: number): Promise<{ access_token: string }> {
    return req<{ access_token: string }>(`/${pageId}/regenerate-token`, { method: 'POST' });
  },
  // inbox-499 — лиды (mail_form widget submissions, backend pending).
  listLeads(pageId: number, limit = 50): Promise<BioLead[]> {
    return req<BioLead[]>(`/${pageId}/leads?limit=${limit}`);
  },
  // inbox-499 — CRUD translations (backend pending).
  upsertTranslation(pageId: number, body: BioTranslationPatch): Promise<BioTranslation> {
    return req<BioTranslation>(`/${pageId}/translations`, { method: 'POST', body: JSON.stringify(body) });
  },
  deleteTranslation(trId: number): Promise<void> {
    return req<void>(`/translations/${trId}`, { method: 'DELETE' });
  },
};

// inbox-498 — public submit для mail_form widget (без auth, через slug).
// Honeypot валидация на backend; rate-limit 5/IP/час.
export interface MailFormSubmit {
  email: string;
  /** honeypot — должно быть пустым (если заполнено — бот, backend silently drop). */
  hp?: string;
}

export interface BioLead {
  id: number;
  bio_page_id: number;
  email: string;
  created_at: string;
  ip?: string;
  user_agent?: string;
}

export interface BioTranslation {
  id: number;
  bio_page_id: number;
  widget_id: number | null;
  lang: string;
  payload_json: Record<string, unknown>;
}

export interface BioTranslationPatch {
  widget_id?: number | null;
  lang: string;
  payload_json: Record<string, unknown>;
}

export async function submitMailForm(slug: string, body: MailFormSubmit): Promise<{ ok: boolean }> {
  const res = await fetch(`/api/v1/bio/public/${encodeURIComponent(slug)}/leads`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'omit',
  });
  if (res.status === 429) throw new ApiError(429, 'Слишком много запросов. Попробуйте через час.');
  if (!res.ok) throw new ApiError(res.status, 'Не удалось отправить');
  return res.status === 204 ? { ok: true } : ((await res.json()) as { ok: boolean });
}

// ─── Widget palette (статичный config — какие типы виджетов
//     можно добавить из палитры).
export interface WidgetPaletteItem {
  type: string;
  label: string;
  icon: string;
  defaultConfig: Record<string, unknown>;
  description: string;
}

export const WIDGET_PALETTE: WidgetPaletteItem[] = [
  {
    type: 'link',
    label: 'Ссылка',
    icon: '🔗',
    description: 'Кнопка с заголовком и иконкой',
    defaultConfig: { title: 'Кнопка', url: 'https://', icon: 'arrow-right', style: 'filled' },
  },
  {
    type: 'video',
    label: 'Видео',
    icon: '▶︎',
    description: 'YouTube / VK Видео / MP4',
    defaultConfig: { source: 'youtube', video_id: '' },
  },
  {
    type: 'form',
    label: 'Форма',
    icon: '✉️',
    description: 'Сбор email или телефонов',
    defaultConfig: { title: 'Подписка', fields: ['email'], submit_label: 'Отправить' },
  },
  {
    type: 'product',
    label: 'Продукт',
    icon: '🛍',
    description: 'Карточка товара с CTA',
    defaultConfig: { name: 'Продукт', price: 0, currency: 'RUB', image_url: '', cta: 'Купить' },
  },
  {
    type: 'reviews',
    label: 'Отзывы',
    icon: '⭐',
    description: 'Карусель оценок',
    defaultConfig: { items: [] },
  },
  {
    type: 'audio',
    label: 'Аудио',
    icon: '🎵',
    description: 'Подкаст / трек',
    defaultConfig: { source: 'soundcloud', url: '' },
  },
  {
    type: 'contact',
    label: 'Контакты',
    icon: '📇',
    description: 'vCard блок',
    defaultConfig: { phones: [], emails: [], messengers: [] },
  },
  {
    type: 'embed',
    label: 'Embed',
    icon: '⌘',
    description: 'HTML-вставка',
    defaultConfig: { html: '' },
  },
  // inbox-498 — Tips: donation button (external Boosty/CloudTips/etc).
  {
    type: 'tips',
    label: 'Поддержать',
    icon: '♥',
    description: 'Кнопка donation на внешний сервис',
    defaultConfig: { title: 'Поддержать автора', url: 'https://', icon: 'heart' },
  },
  // inbox-498 — MailForm: email collection с honeypot.
  {
    type: 'mail_form',
    label: 'Email-форма',
    icon: '✉',
    description: 'Сбор email с защитой honeypot',
    defaultConfig: {
      title: 'Подписаться на новости',
      placeholder: 'email@example.com',
      submit_label: 'Подписаться',
      success_message: 'Спасибо!',
    },
  },
  // inbox-498 — Social: одна платформа с brand icon + faceValue.
  {
    type: 'social',
    label: 'Соцсеть',
    icon: '@',
    description: 'Иконка платформы с @username',
    defaultConfig: { platform: 'instagram', url: 'https://instagram.com/', face_value: '' },
  },
];
